import React, { useCallback, useEffect, useState } from "react";
import { usePlayer } from "../../../../Contexts/playerData.context";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { getUserFromToken } from "../../../../utils/functions.ts";
import DragBox2026 from "../../../Universals/DragBox/DragBox2026/dragBox2026.component.jsx";
import "./quickNotes.style.scss";

const QUICK_NOTES_ENDPOINT = "https://www.fbxchange.com/ReactPHP/quickNotes.php";
const QUICK_NOTES_REFRESH_EVENT = "FBX_QUICK_NOTES_CHANGED";

const cleanText = (value) => String(value ?? "").trim();

const getScoutName = (user) => cleanText(
  user?.displayName ||
  user?.scoutName ||
  user?.name ||
  user?.firstName ||
  user?.userName ||
  ""
);

const formatQuickNoteDate = (value) => {
  const text = cleanText(value);
  if (!text || text === "0000-00-00" || text === "0000-00-00 00:00:00") return "";
  const normalized = text.includes("T") ? text : text.replace(" ", "T");
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const QuickNotes = () => {
  const { playerID, activePlayer } = usePlayer();
  const { updateActiveSideButton } = useNavBar();
  const [user, setUser] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [draftText, setDraftText] = useState("");
  const [versionIndex, setVersionIndex] = useState(0);

  const activeFBXID = cleanText(playerID || activePlayer?.FBXID);
  const playerName = [activePlayer?.last || activePlayer?.LAST, activePlayer?.first || activePlayer?.FIRST]
    .map(cleanText)
    .filter(Boolean)
    .join(", ");
  const defaultPosition = React.useMemo(() => ({
    x: typeof window !== "undefined" ? Math.max(window.innerWidth - 440, 20) : 860,
    y: 90,
  }), []);

  const notifyQuickNotesChanged = useCallback((nextNotes = notes) => {
    window.dispatchEvent(new CustomEvent(QUICK_NOTES_REFRESH_EVENT, {
      detail: {
        FBXID: activeFBXID,
        hasQuickNote: Array.isArray(nextNotes) && nextNotes.length > 0 ? "Y" : "N",
        quickNoteCount: Array.isArray(nextNotes) && nextNotes.length > 0 ? 1 : 0,
        versionCount: Array.isArray(nextNotes) ? nextNotes.length : 0,
      },
    }));
  }, [activeFBXID, notes]);

  const loadQuickNotes = useCallback(async () => {
    if (!activeFBXID) return;
    const tokenUser = await getUserFromToken();
    if (!tokenUser?.team || !tokenUser?.scout) {
      setMessage("Sign in required");
      return;
    }

    setUser(tokenUser);
    setLoading(true);
    setMessage("");

    try {
      const response = await fetch(QUICK_NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scriptName: "getQuickNotes",
          team: tokenUser.team,
          scoutId: tokenUser.scout,
          FBXID: activeFBXID,
        }),
      });
      const data = await response.json();
      if (data?.success) {
        const nextNotes = Array.isArray(data.notes) ? data.notes : [];
        setNotes(nextNotes);
        setVersionIndex(0);
        setDraftText(nextNotes[0]?.noteText || "");
        return nextNotes;
      } else {
        setNotes([]);
        setVersionIndex(0);
        setDraftText("");
        setMessage(data?.message || "Unable to load quick notes");
        return [];
      }
    } catch {
      setNotes([]);
      setVersionIndex(0);
      setDraftText("");
      setMessage("Unable to load quick notes");
      return [];
    } finally {
      setLoading(false);
    }
  }, [activeFBXID]);

  useEffect(() => {
    const handleQuickNotesChanged = (event) => {
      const changedFBXID = cleanText(event?.detail?.FBXID);
      if (!changedFBXID || changedFBXID === activeFBXID) loadQuickNotes();
    };

    loadQuickNotes();
    window.addEventListener(QUICK_NOTES_REFRESH_EVENT, handleQuickNotesChanged);

    return () => {
      window.removeEventListener(QUICK_NOTES_REFRESH_EVENT, handleQuickNotesChanged);
    };
  }, [activeFBXID, loadQuickNotes]);

  const runQuickNoteAction = async (payload, successMessage = "") => {
    if (!user?.team || !user?.scout || !activeFBXID) return;

    setSaving(true);
    setMessage("");
    try {
      const response = await fetch(QUICK_NOTES_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          team: user.team,
          scoutId: user.scout,
          scoutName: getScoutName(user),
          FBXID: activeFBXID,
          ...payload,
        }),
      });
      const data = await response.json();
      if (!data?.success) {
        setMessage(data?.message || "Quick note action failed");
        return false;
      }
      setMessage(successMessage || data?.message || "Saved");
      const nextNotes = await loadQuickNotes();
      notifyQuickNotesChanged(nextNotes);
      return true;
    } catch {
      setMessage("Quick note action failed");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const viewedNote = notes[versionIndex] || null;
  const latestNote = notes[0] || null;
  const isViewingLatest = versionIndex === 0;
  const versionCount = notes.length;
  const canSaveVersion = cleanText(draftText) && cleanText(draftText) !== cleanText(latestNote?.noteText);

  const saveQuickNote = async () => {
    const noteText = cleanText(draftText);
    if (!noteText) {
      setMessage("Enter a quick note first");
      return;
    }

    if (latestNote && !canSaveVersion) {
      setMessage("No QN changes to save");
      return;
    }

    const payload = {
      noteText,
      ...(latestNote
        ? { scriptName: "updateQuickNote", quickNoteId: latestNote.quickNoteId }
        : { scriptName: "createQuickNote" }),
    };
    await runQuickNoteAction(payload, latestNote ? "Quick note version saved" : "Quick note added");
  };

  const deleteQuickNote = async (note) => {
    const ok = await runQuickNoteAction({
      scriptName: "deleteQuickNote",
      quickNoteId: note.quickNoteId,
    }, "Quick note deleted");
    if (ok) setVersionIndex(0);
  };

  const goToVersion = (nextIndex) => {
    const boundedIndex = Math.min(Math.max(nextIndex, 0), Math.max(notes.length - 1, 0));
    setVersionIndex(boundedIndex);
    setDraftText(notes[boundedIndex]?.noteText || "");
    setMessage("");
  };

  return (
    <DragBox2026
      storageKey="QUICK_NOTES_DRAGBOX_POS_2026"
      defaultPos={defaultPosition}
      title={playerName || activeFBXID}
      minWidth={420}
      maxWidth={420}
      zIndex={996}
      showReset={true}
      onClose={() => updateActiveSideButton("")}
    >
      <div className="quick-notes-box">

      {message && <div className="quick-notes-message">{message}</div>}

      <div className="quick-notes-list">
        {loading ? (
          <div className="quick-notes-empty">Loading quick notes...</div>
        ) : (
          <div className="quick-notes-note">
            <div className="quick-notes-add-title">Quick Note</div>
            <textarea
              className="quick-notes-note-editor"
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              placeholder="Add a quick note"
              disabled={saving || loading}
            />

            {viewedNote ? (
              <div className="quick-notes-meta">
                <div>
                  <span>{isViewingLatest ? "Current:" : "Version:"}</span> edited by {cleanText(viewedNote.updatedByScoutName || viewedNote.createdByScoutName) || `Scout ${viewedNote.updatedByScoutId || viewedNote.createdByScoutId}`} {formatQuickNoteDate(viewedNote.updatedAt || viewedNote.createdAt)}
                </div>
              </div>
            ) : (
              <div className="quick-notes-empty">No quick note yet.</div>
            )}

            <div className="quick-notes-version-bar">
              <button type="button" onClick={() => goToVersion(versionIndex + 1)} disabled={saving || versionIndex >= versionCount - 1}>
                {"<"}
              </button>
              <div>{versionCount > 0 ? `${versionIndex + 1} of ${versionCount}` : "0 of 0"}</div>
              <button type="button" onClick={() => goToVersion(versionIndex - 1)} disabled={saving || versionIndex <= 0}>
                {">"}
              </button>
            </div>

            <div className="quick-notes-actions">
              <div className="quick-notes-version-status">
                {isViewingLatest ? "Newest QN" : "Viewing older QN"}
              </div>

              <div className="quick-notes-action-buttons">
                <button
                  type="button"
                  className="quick-notes-save-button"
                  onClick={saveQuickNote}
                  disabled={saving || loading || !cleanText(draftText) || (latestNote && !canSaveVersion)}
                >
                  Save
                </button>
                {!isViewingLatest && (
                  <button type="button" className="quick-notes-edit-button" onClick={() => goToVersion(0)} disabled={saving}>
                    Newest
                  </button>
                )}
                {viewedNote?.canDelete && (
                  <button type="button" className="quick-notes-delete" onClick={() => deleteQuickNote(viewedNote)} disabled={saving}>
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      </div>
    </DragBox2026>
  );
};

export default QuickNotes;
